import React, { Component, createRef } from "react";
import FlyerImg from '../../images/FlyerLeft.png';
import { jsPDF } from 'jspdf';
import CardBack from "../Summary/CardBack";
import CardFront from "../Summary/CardFront";

// ─── Placeholder pixel coordinates in the native 2550×3300 FlyerLeft.png image ───
// Found by pixel scan: white rectangle starts at y=2380, ends at y=3207 (full width)
const FLYER_W = 2550;
const FLYER_H = 3300;
const PH = { x1: 0, y1: 2380, x2: 2550, y2: 3207 }; // full-width placeholder

class FlyerPage1 extends Component {
    constructor(props) {
        super(props);
        this.containerRef = createRef();
        this.imgRef = createRef();
        this.state = { cardStyle: null };
        this.handleFlyerLoad = this.handleFlyerLoad.bind(this);
        this.downloaddoc = this.downloaddoc.bind(this);
        this.printIndivisual = this.printIndivisual.bind(this);
        this._ro = null;
    }

    componentDidMount() {
        if (this.containerRef.current && typeof ResizeObserver !== 'undefined') {
            this._ro = new ResizeObserver(() => this.handleFlyerLoad());
            this._ro.observe(this.containerRef.current);
        }
    }

    componentWillUnmount() {
        if (this._ro) this._ro.disconnect();
    }

    // When image loads (or on resize), compute exact CSS positions from native pixel coords
    handleFlyerLoad() {
        const img = this.imgRef.current;
        if (!img) return;
        const sx = img.clientWidth  / FLYER_W;
        const sy = img.clientHeight / FLYER_H;
        this.setState({
            cardStyle: {
                position: 'absolute',
                left:   Math.round(PH.x1 * sx) + 20 + 'px',
                top:    Math.round(PH.y1 * sy) + 4 + 'px',
                width:  Math.round((PH.x2 - PH.x1) * sx) + 'px',
                height: Math.round((PH.y2 - PH.y1) * sy) + 'px',
            }
        });
    }

    // Helper: ensure an img element is loaded
    waitForImg(imgEl) {
        return new Promise(res => {
            if (!imgEl) { res(); return; }
            if (imgEl.complete && imgEl.naturalWidth > 0) { res(); return; }
            imgEl.onload  = res;
            imgEl.onerror = res;
        });
    }

    // Build composite canvas: FlyerLeft.png base + cards drawn at exact placeholder coords
    async composeCanvas() {
        const container = this.containerRef.current;
        if (!container) return null;

        const flyerImgEl = this.imgRef.current;
        // Look for cards both inside the positioned overlay (visible) and anywhere in the container (hidden)
        const frontCanvas = container.querySelector('canvas');
        const backImgEl   = container.querySelector('.flyer-cards-area img') ||
                            container.querySelector('img[alt="Prescription Savings Card — Back"]');

        await this.waitForImg(flyerImgEl);
        await this.waitForImg(backImgEl);

        const scale = 2; // 2× for PDF sharpness
        const composite = document.createElement('canvas');
        composite.width  = FLYER_W * scale;
        composite.height = FLYER_H * scale;
        const ctx = composite.getContext('2d');
        ctx.scale(scale, scale);

        // Helper: draw an image/canvas clipped to a rounded rectangle
        const drawRounded = (src, x, y, w, h, r) => {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(src, x, y, w, h);
            ctx.restore();
        };

        // 1. Draw the full flyer image as base
        if (flyerImgEl) ctx.drawImage(flyerImgEl, 0, 0, FLYER_W, FLYER_H);

        // 2. Compute card draw area in native pixels
        // Each card fills half the placeholder width (flex: 1), minus a 16px gap
        // 16px CSS gap at 700px display → 16*(2550/700) ≈ 58px native
        const phW      = PH.x2 - PH.x1;
        const phH      = PH.y2 - PH.y1;
        const gapPx    = Math.round(16 * (FLYER_W / 700)); // 58px native
        const cardW    = Math.round((phW - gapPx) / 2);    // each card = half width
        const cardH    = Math.round(cardW * (454 / 772));   // maintain card aspect ratio
        const cardY    = PH.y1 + Math.round((phH - cardH) / 2); // center vertically
        const radius   = Math.round(16 * (FLYER_W / 700)); // 16px CSS border-radius in native px

        // 3. Draw front card with rounded corners
        if (frontCanvas) {
            drawRounded(frontCanvas, PH.x1, cardY, cardW, cardH, radius);
        }

        // 4. Draw back card with rounded corners
        if (backImgEl) {
            try { drawRounded(backImgEl, PH.x1 + cardW + gapPx, cardY, cardW, cardH, radius); }
            catch (e) { /* skip cross-origin */ }
        }

        return composite;
    }

    async downloaddoc() {
        const composite = await this.composeCanvas();
        if (!composite) return;
        const pdf  = new jsPDF('p', 'mm', 'a4');
        const pdfW = pdf.internal.pageSize.getWidth();
        const pdfH = pdf.internal.pageSize.getHeight();
        const ratio = Math.min((pdfW - 10) / (composite.width / 2), (pdfH - 10) / (composite.height / 2));
        const w = (composite.width  / 2) * ratio;
        const h = (composite.height / 2) * ratio;
        pdf.addImage(composite.toDataURL('image/jpeg', 0.95), 'JPEG', (pdfW - w) / 2, (pdfH - h) / 2, w, h);
        pdf.save('flyer-1.pdf');
    }

    async printIndivisual() {
        const composite = await this.composeCanvas();
        if (!composite) return;
        const imgSrc = composite.toDataURL('image/png');
        document.body.innerHTML = `<div style="text-align:center;padding:20px;"><img src="${imgSrc}" style="max-width:700px;width:100%;height:auto;display:inline-block;" /></div>`;
        setTimeout(() => { window.print(); window.location.reload(); }, 500);
    }

    render() {
        const { cardStyle } = this.state;
        return (
            <div ref={this.containerRef} style={{ position: 'relative', lineHeight: 0 }}>
                {/* Download / Print buttons */}
                <div className="buttons">
                    <div className="download" onClick={this.downloaddoc}></div>
                    <div className="print"    onClick={this.printIndivisual}></div>
                </div>

                {/* Full flyer image as base */}
                <img
                    ref={this.imgRef}
                    src={FlyerImg}
                    alt="Flyer 1"
                    onLoad={this.handleFlyerLoad}
                    style={{ width: '100%', display: 'block', height: 'auto' }}
                />

                {/* Cards overlaid on the white placeholder rectangle */}
                {cardStyle && (
                    <div
                        className="flyer-cards-area"
                        style={{
                            ...cardStyle,
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                            background: 'transparent',
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <CardFront
                                bin={this.props.bin}
                                pcn={this.props.pcn}
                                classnames={this.props.classnames}
                                PrimaryGn={this.props.groupNumber}
                                mId={this.props.mId}
                            />
                </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <CardBack
                                bin={this.props.bin}
                                pcn={this.props.pcn}
                                classnames={this.props.classnames}
                                groupData={this.props.groupData}
                                groupNumber={this.props.groupNumber}
                            />
                </div>
                </div>
                )}
            </div>
        );
    }
}

export default FlyerPage1;